import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { getLocale, useT, tr } from "@/lib/i18n";
import { sectorName } from "@/lib/resultsFr";
import { downloadPdf } from "@/lib/pdfDownload";
import { applicationLabel } from "@/lib/application";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Lock, FileText, FileSpreadsheet, GitCompare, Globe, Building2, ChevronDown, RotateCcw, Archive, AlertTriangle, Clock, Pencil, Check, X, Search } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PurchaseModal } from "@/components/PurchaseModal";
import { ConfirmUnlockModal } from "@/components/ConfirmUnlockModal";
import { ReferralCard } from "@/components/ReferralCard";
import { comparisonYears } from "@/lib/comparison";
import { num, money, moneyM, pct, unit, perUnit, perYr } from "@/lib/numfmt";

const usd = (n) => `${money(n || 0, 0)}`;

// iter45n — Relative time formatter for the "Updated X ago" chip on
// re-edited project cards. Kept intentionally simple (no i18n plural
// packages) — buyers scan the chip for freshness, not precision, and
// the absolute ISO timestamp sits behind the tooltip on the chip.
const formatRelativeTime = (iso) => {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const diffSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  const fr = getLocale() === "fr";
  if (diffSec < 5) return fr ? "à l'instant" : "just now";
  if (diffSec < 60) return fr ? `il y a ${diffSec}\u00a0s` : `${diffSec} sec ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return fr ? `il y a ${diffMin}\u00a0min` : `${diffMin} min ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return fr ? `il y a ${diffH}\u00a0h` : `${diffH} hr ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return fr ? `il y a ${diffD}\u00a0jour${diffD === 1 ? "" : "s"}` : `${diffD} day${diffD === 1 ? "" : "s"} ago`;
  const diffMo = Math.floor(diffD / 30);
  if (diffMo < 12) return fr ? `il y a ${diffMo}\u00a0mois` : `${diffMo} mo ago`;
  const diffY = Math.floor(diffMo / 12);
  return fr ? `il y a ${diffY}\u00a0an${diffY === 1 ? "" : "s"}` : `${diffY} yr${diffY === 1 ? "" : "s"} ago`;
};

/** Read the app-level language toggle at click time so the PDF URL always
 * reflects the customer's current UI choice (not the browser's Accept-Language
 * header, which the CDN + preview environment sometimes strip). */
const currentAppLang = () => getLocale();

const FILTER_STORAGE_KEY = "fev.projects.filters";

const readSavedFilters = () => {
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return { country: "all", sector: "all", sort: "newest" };
    const parsed = JSON.parse(raw);
    return {
      country: typeof parsed?.country === "string" ? parsed.country : "all",
      sector: typeof parsed?.sector === "string" ? parsed.sector : "all",
      // iter45o + iter45q — Persist the sort choice across sessions so a
      // buyer who bumped Recently updated / Largest peak once stays on that view.
      sort: (parsed?.sort === "recent" || parsed?.sort === "peak") ? parsed.sort : "newest",
    };
  } catch {
    return { country: "all", sector: "all", sort: "newest" };
  }
};

export const Projects = () => {
  const t = useT(); // the PDF default follows the language switch while this page is open
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [purchaseModal, setPurchaseModal] = useState({ open: false, projectId: null });
  const [confirmModal, setConfirmModal] = useState({ open: false, project: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, project: null, busy: false });
  const [projects, setProjects] = useState([]);
  const [deletedProjects, setDeletedProjects] = useState([]);
  const [versionStatus, setVersionStatus] = useState({});
  // Per-card PDF language override — starts at the on-screen language (getLocale) so the
  // header toggle still controls the default, but the buyer can flip a single
  // card without switching the whole UI (useful when sending one FR report to
  // a francophone banker while browsing the rest in English).
  const [pdfLangByProject, setPdfLangByProject] = useState({});
  const [busy, setBusy] = useState(true);
  const [countryFilter, setCountryFilter] = useState(() => readSavedFilters().country);
  const [sectorFilter, setSectorFilter] = useState(() => readSavedFilters().sector);
  // iter45o — "Newest" (default: created_at desc, matches the backend
  // list order) vs "Recently updated" (max(updated_at, created_at) desc)
  // so re-edited projects bubble to the top. iter45q — added "Largest peak"
  // (peak_kw desc) so portfolio managers can group by site size.
  const [sortMode, setSortMode] = useState(() => readSavedFilters().sort);

  // iter45r — Live-tick: force a re-render every 30 s so the "Updated X
  // ago" chip on cards stays fresh on a long-open dashboard. `Date.now()`
  // is only read inside `formatRelativeTime` so we just need something
  // in state that changes. `now` unused elsewhere; the setState call is
  // what re-runs the JSX.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  // iter45p — Inline rename state. `renameId` = project currently being
  // edited (null = none). `renameDraft` = current text-input value.
  const [renameId, setRenameId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);

  // iter45s — Free-text filter. Matches (case-insensitive substring) on
  // the project name plus its sector / country / application inputs so
  // buyers can find a card by any label that's visible on it. Kept as
  // ephemeral state (not persisted) because a "sticky search" would
  // silently hide projects on the next visit.
  const [searchQuery, setSearchQuery] = useState("");
  const startRename = (p) => { setRenameId(p.id); setRenameDraft(p.name || ""); };
  const cancelRename = () => { setRenameId(null); setRenameDraft(""); };
  const commitRename = async (p) => {
    const next = renameDraft.trim();
    if (!next || next === (p.name || "").trim()) { cancelRename(); return; }
    setRenameBusy(true);
    try {
      const r = await api.patch(`/projects/${p.id}/rename`, { name: next });
      if (r.data?.ok) {
        setProjects((rows) => rows.map((row) => row.id === p.id ? { ...row, name: r.data.name } : row));
      } else {
        toast.error(r.data?.error || tr("Could not rename", "Renommage impossible"));
      }
    } catch (e) { toast.error(tr("Rename failed", "Échec du renommage")); }
    finally { setRenameBusy(false); cancelRename(); }
  };

  // Persist filters so users return to the view they left. Storage is a boundary; guard it.
  useEffect(() => {
    try {
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({ country: countryFilter, sector: sectorFilter, sort: sortMode }));
    } catch { /* private mode / disabled storage */ }
  }, [countryFilter, sectorFilter, sortMode]);

  const load = () => api.get("/projects").then(async (r) => {
    const projs = r.data || [];
    setProjects(projs);
    // Stage 2b: batch-check version status so paid projects can show "Updated, new report required"
    const statuses = {};
    await Promise.all(projs.filter((p) => p.paid).map(async (p) => {
      try {
        const { data } = await api.get(`/projects/${p.id}/version-status`);
        statuses[p.id] = data;
      } catch (e) {
        // Per-project check; one failure must not blank the whole list. Logged
        // because a silent swallow here hid nothing useful during debugging.
        console.error(`version-status failed for project ${p.id}`, e);
      }
    }));
    setVersionStatus(statuses);
    // Recently deleted bin (fire-and-forget — bin failure shouldn't block the main list)
    try {
      const { data: bin } = await api.get("/projects/deleted");
      setDeletedProjects(bin || []);
    } catch { /* non-fatal */ }
  }).catch(() => toast.error(tr("Could not load projects", "Impossible de charger les projets")));

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    load().finally(() => setBusy(false));
  }, [user, loading, navigate]);

  // { country: count } — memoised so filter chips only rebuild when project list changes.
  const countryCounts = useMemo(() => {
    const counts = {};
    for (const p of projects) {
      const c = p.input?.country || "Unknown";
      counts[c] = (counts[c] || 0) + 1;
    }
    return counts;
  }, [projects]);

  const sectorCounts = useMemo(() => {
    const counts = {};
    for (const p of projects) {
      const s = p.input?.sector || "Unknown";
      counts[s] = (counts[s] || 0) + 1;
    }
    return counts;
  }, [projects]);

  const countryOptions = useMemo(() => Object.keys(countryCounts).sort(), [countryCounts]);
  const sectorOptions = useMemo(() => Object.keys(sectorCounts).sort(), [sectorCounts]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const rows = projects.filter((p) => {
      const c = p.input?.country || "Unknown";
      const s = p.input?.sector || "Unknown";
      if (countryFilter !== "all" && c !== countryFilter) return false;
      if (sectorFilter !== "all" && s !== sectorFilter) return false;
      // iter45s — Free-text search. Match against name + all card-visible
      // input labels (sector, country, application). Empty query = no
      // filtering applied.
      if (q) {
        const hay = [
          p.name, p.input?.sector, p.input?.country, applicationLabel(p.input || {}),
        ].filter(Boolean).join(" · ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    if (sortMode === "peak") {
      // iter45q — Largest peak first; use `.slice()` to avoid mutating
      // the source array (we still need original order for other memos).
      return rows.slice().sort((a, b) => {
        const pa = Number(a.input?.peak_kw || 0);
        const pb = Number(b.input?.peak_kw || 0);
        return pb - pa;
      });
    }
    if (sortMode !== "recent") return rows;
    // iter45o — Sort by the more recent of `updated_at` or `created_at`
    // so re-edited configs float to the top. `.slice()` first because
    // `.sort` mutates in place and we don't want to reorder the source
    // `projects` array (breaks the country/sector counts memo).
    return rows.slice().sort((a, b) => {
      const ta = a.updated_at || a.created_at || "";
      const tb = b.updated_at || b.created_at || "";
      return ta < tb ? 1 : ta > tb ? -1 : 0;
    });
  }, [projects, countryFilter, sectorFilter, sortMode, searchQuery]);

  // Two-step delete: clicking the trash icon opens the confirm dialog. Actual
  // soft-delete happens in `confirmDelete()` on the "Move to bin" action.
  const remove = (p) => setDeleteConfirm({ open: true, project: p, busy: false });

  const confirmDelete = async () => {
    const p = deleteConfirm.project;
    if (!p) return;
    setDeleteConfirm((s) => ({ ...s, busy: true }));
    try {
      const { data } = await api.delete(`/projects/${p.id}`);
      setProjects((ps) => ps.filter((x) => x.id !== p.id));
      setDeletedProjects((bin) => [
        {
          ...p,
          deleted_at: data.deleted_at,
          deleted_expires_at: data.deleted_expires_at,
        },
        ...bin,
      ]);
      toast.success(tr("Moved to Recently deleted", "Déplacé vers Récemment supprimés"), {
        description: tr("You have 7 days to restore.", "Vous avez 7\u00a0jours pour le restaurer."),
      });
      setDeleteConfirm({ open: false, project: null, busy: false });
    } catch {
      toast.error(tr("Delete failed", "Échec de la suppression"));
      setDeleteConfirm((s) => ({ ...s, busy: false }));
    }
  };

  const restore = async (p) => {
    try {
      await api.post(`/projects/${p.id}/restore`);
      setDeletedProjects((bin) => bin.filter((x) => x.id !== p.id));
      // Drop the deleted_* fields so it looks identical to a freshly-listed project.
      const restored = { ...p };
      delete restored.deleted_at;
      delete restored.deleted_expires_at;
      setProjects((ps) => [restored, ...ps]);
      toast.success(tr(`Restored “${p.name || "Untitled"}”`, `«\u00a0${p.name || "Sans titre"}\u00a0» restauré`));
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Restore failed", "Échec de la restauration"));
    }
  };

  const openReport = async (p) => {
    if (p.paid) { navigate(`/report/${p.id}`); return; }
    // Not paid: if wallet has credits → confirmation modal; else → purchase pop-up.
    if ((user?.credits || 0) > 0) {
      setConfirmModal({ open: true, project: p });
    } else {
      setPurchaseModal({ open: true, projectId: p.id });
    }
  };

  return (
    <div className="App">
      <Header />
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-10">
        <div className="flex justify-between items-end gap-4 flex-wrap mb-8">
          <div>
            <div className="overline mb-2">{tr("Saved", "Enregistrés")}</div>
            <h1 className="font-display text-3xl md:text-4xl">{tr("My projects", "Mes projets")}</h1>
            {user?.credits > 0 && (
              <p className="text-sm mt-2" style={{ color: "var(--muted)" }}>
                {tr("You have", "Vous avez")} <span className="pill" style={{ background: "var(--paper)", color: "var(--brand-green)" }} data-testid="credit-balance">{getLocale() === "fr" ? `${user.credits}\u00a0crédit${user.credits === 1 ? "" : "s"} de rapport` : `${user.credits} report credit${user.credits === 1 ? "" : "s"}`}</span> {tr("available.", user.credits === 1 ? "disponible." : "disponibles.")}
              </p>
            )}
          </div>
          <button onClick={() => navigate("/configurator")} className="btn-primary" data-testid="btn-new">
            <Plus size={14} /> {tr("New configuration", "Nouvelle configuration")}
          </button>
        </div>

        {/* Two-sided referral rewards — auth-gated card shown on Projects (Wallet area). */}
        {user && (
          <div className="mb-8" data-testid="referral-card-slot-projects">
            <ReferralCard variant="wallet" />
          </div>
        )}

        {projects.length >= 2 && (
          <div className="card p-4 mb-6 flex flex-wrap items-center justify-between gap-3" style={{ background: "var(--paper)" }}>
            <div className="flex items-center gap-3">
              <GitCompare size={16} style={{ color: "var(--brand-green)" }} />
              <div>
                <div className="font-display text-base" style={{ color: "var(--charcoal)" }}>{tr("Compare scenarios side-by-side", "Comparer des scénarios côte à côte")}</div>
                <div className="text-sm" style={{ color: "var(--muted)" }}>{tr("Rank 2–4 saved projects by LCOE, CAPEX and payback.", "Classez 2 à 4\u00a0projets enregistrés par LCOE, CAPEX et retour sur investissement.")}</div>
              </div>
            </div>
            <button onClick={() => navigate(`/compare?ids=${projects.slice(0, 2).map(p => p.id).join(",")}`)} className="btn-secondary" data-testid="btn-compare">
              <GitCompare size={14} /> {tr("Open comparison", "Ouvrir la comparaison")}
            </button>
          </div>
        )}

        {/* iter45s — Filter-by-name search. Only rendered when there
             are 3+ saved projects; below that a search box is more UI
             than value. Kept above the chip rows so it reads as the
             primary find-affordance for long portfolios. */}
        {projects.length >= 3 && (
          <div className="mb-3 flex items-center gap-2" data-testid="search-row">
            <div className="relative flex-1 max-w-md">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                       style={{ color: "var(--muted)" }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={tr(`Search ${projects.length} projects by name, country or sector…`, `Rechercher parmi ${projects.length}\u00a0projets par nom, pays ou secteur…`)}
                className="w-full pl-8 pr-8 py-2 text-sm rounded"
                style={{ background: "var(--white)", border: "1px solid var(--hairline)" }}
                data-testid="search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  aria-label={tr("Clear search", "Effacer la recherche")}
                  data-testid="search-clear"
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                  style={{ background: "transparent", border: "none",
                            color: "var(--muted)", cursor: "pointer", padding: 0 }}
                >
                  <X size={13} />
                </button>
              )}
            </div>
          </div>
        )}

        {countryOptions.length >= 2 && (
          <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="country-filter">
            <div className="flex items-center gap-1.5 mr-2 text-xs" style={{ color: "var(--muted)" }}>
              <Globe size={12} /> {tr("Country", "Pays")}
            </div>
            <button
              onClick={() => setCountryFilter("all")}
              className="pill transition-colors"
              style={{
                background: countryFilter === "all" ? "var(--brand-green)" : "var(--paper)",
                color: countryFilter === "all" ? "white" : "var(--body)",
                border: "1px solid " + (countryFilter === "all" ? "var(--brand-green)" : "var(--hairline)"),
                cursor: "pointer",
              }}
              data-testid="country-filter-all"
            >
              {tr("All", "Tous")} · {projects.length}
            </button>
            {countryOptions.map((c) => (
              <button
                key={c}
                onClick={() => setCountryFilter(c)}
                className="pill transition-colors"
                style={{
                  background: countryFilter === c ? "var(--brand-green)" : "var(--paper)",
                  color: countryFilter === c ? "white" : "var(--body)",
                  border: "1px solid " + (countryFilter === c ? "var(--brand-green)" : "var(--hairline)"),
                  cursor: "pointer",
                }}
                data-testid={`country-filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {getLocale() === "fr" ? t(`country.${c}`, c) : c} · {countryCounts[c]}
              </button>
            ))}
          </div>
        )}

        {sectorOptions.length >= 2 && (
          <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="sector-filter">
            <div className="flex items-center gap-1.5 mr-2 text-xs" style={{ color: "var(--muted)" }}>
              <Building2 size={12} /> {tr("Sector", "Secteur")}
            </div>
            <button
              onClick={() => setSectorFilter("all")}
              className="pill transition-colors"
              style={{
                background: sectorFilter === "all" ? "var(--brand-green)" : "var(--paper)",
                color: sectorFilter === "all" ? "white" : "var(--body)",
                border: "1px solid " + (sectorFilter === "all" ? "var(--brand-green)" : "var(--hairline)"),
                cursor: "pointer",
              }}
              data-testid="sector-filter-all"
            >
              {tr("All", "Tous")} · {projects.length}
            </button>
            {sectorOptions.map((s) => (
              <button
                key={s}
                onClick={() => setSectorFilter(s)}
                className="pill transition-colors"
                style={{
                  background: sectorFilter === s ? "var(--brand-green)" : "var(--paper)",
                  color: sectorFilter === s ? "white" : "var(--body)",
                  border: "1px solid " + (sectorFilter === s ? "var(--brand-green)" : "var(--hairline)"),
                  cursor: "pointer",
                }}
                data-testid={`sector-filter-${s.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {sectorName(s)} · {sectorCounts[s]}
              </button>
            ))}
          </div>
        )}

        {/* iter45o — Sort by Newest (default: created_at desc) or
             Recently updated so re-edited projects float to the top.
             Only rendered when the buyer has more than one saved
             project — a single card has nothing to sort. */}
        {projects.length >= 2 && (
          <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="sort-mode">
            <div className="flex items-center gap-1.5 mr-2 text-xs" style={{ color: "var(--muted)" }}>
              <Clock size={12} /> {tr("Sort", "Trier")}
            </div>
            {[
              { id: "newest", label: tr("Newest", "Plus récents") },
              { id: "recent", label: tr("Recently updated", "Modifiés récemment") },
              { id: "peak", label: tr("Largest peak", "Plus forte pointe") },
            ].map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setSortMode(id)}
                className="pill transition-colors"
                style={{
                  background: sortMode === id ? "var(--brand-green)" : "var(--paper)",
                  color: sortMode === id ? "white" : "var(--body)",
                  border: "1px solid " + (sortMode === id ? "var(--brand-green)" : "var(--hairline)"),
                  cursor: "pointer",
                }}
                data-testid={`sort-${id}`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {busy ? (
          <div style={{ color: "var(--muted)" }}>{tr("Loading…", "Chargement…")}</div>
        ) : projects.length === 0 ? (
          <div className="card p-16 text-center" data-testid="empty-state">
            <FileText size={36} className="mx-auto" style={{ color: "var(--muted)" }} />
            <div className="font-display text-2xl mt-4">{tr("No projects yet", "Aucun projet pour l'instant")}</div>
            <p className="text-sm mt-2" style={{ color: "var(--muted)" }}>{tr("Run a first-pass configuration to save it here.", "Lancez une analyse gratuite pour l'enregistrer ici.")}</p>
            <button onClick={() => navigate("/configurator")} className="btn-primary mt-6" data-testid="empty-cta">{t("nav.start_cta")}</button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="card p-10 text-center" data-testid="empty-filter-state">
            <Globe size={28} className="mx-auto" style={{ color: "var(--muted)" }} />
            <div className="font-display text-lg mt-3">
              {countryFilter !== "all" && sectorFilter !== "all"
                ? tr(`No ${sectorFilter} projects in ${countryFilter}`, `Aucun projet ${sectorName(sectorFilter)} — ${t(`country.${countryFilter}`, countryFilter)}`)
                : countryFilter !== "all"
                ? tr(`No projects in ${countryFilter}`, `Aucun projet — ${t(`country.${countryFilter}`, countryFilter)}`)
                : tr(`No ${sectorFilter} projects`, `Aucun projet ${sectorName(sectorFilter)}`)}
            </div>
            <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>{tr("Switch filters or clear them to see all projects.", "Changez de filtre ou effacez-les pour voir tous les projets.")}</p>
            <button
              onClick={() => { setCountryFilter("all"); setSectorFilter("all"); }}
              className="btn-secondary mt-4"
              data-testid="empty-filter-clear"
            >{tr("Clear filters", "Effacer les filtres")}</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((p) => (
              <div key={p.id} className="card p-6" data-testid={`project-${p.id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1 min-w-0 pr-2">
                    <div className="overline">{sectorName(p.input?.sector)} · {getLocale() === "fr" ? t(`country.${p.input?.country}`, p.input?.country) : p.input?.country}</div>
                    {/* iter45p — Inline rename. Pencil icon toggles the
                         title into a text input. Enter (or blur) commits;
                         Escape reverts. Blank names are rejected server-
                         side so the card can't lose its title. */}
                    {renameId === p.id ? (
                      <div className="flex items-center gap-1 mt-1">
                        <input
                          type="text"
                          autoFocus
                          value={renameDraft}
                          disabled={renameBusy}
                          onChange={(e) => setRenameDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") commitRename(p);
                            if (e.key === "Escape") cancelRename();
                          }}
                          onBlur={() => commitRename(p)}
                          maxLength={120}
                          className="font-display text-lg px-2 py-0.5 rounded border flex-1 min-w-0"
                          style={{ borderColor: "var(--brand-green)", background: "var(--white)", color: "var(--charcoal)" }}
                          data-testid={`rename-input-${p.id}`}
                        />
                        <button type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => commitRename(p)}
                                data-testid={`rename-save-${p.id}`}
                                style={{ color: "var(--brand-green)", background: "transparent", border: "none", cursor: "pointer" }}>
                          <Check size={15} />
                        </button>
                        <button type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={cancelRename}
                                data-testid={`rename-cancel-${p.id}`}
                                style={{ color: "var(--muted)", background: "transparent", border: "none", cursor: "pointer" }}>
                          <X size={15} />
                        </button>
                      </div>
                    ) : (
                      <div className="font-display text-lg mt-1 flex items-center gap-1.5 group">
                        <span className="truncate" data-testid={`project-name-${p.id}`}>{p.name}</span>
                        <button type="button"
                                onClick={() => startRename(p)}
                                title={tr("Rename", "Renommer")}
                                aria-label={tr("Rename project", "Renommer le projet")}
                                data-testid={`rename-btn-${p.id}`}
                                className="opacity-50 hover:opacity-100 transition-opacity"
                                style={{ color: "var(--muted)", background: "transparent", border: "none", cursor: "pointer", padding: 0 }}>
                          <Pencil size={13} />
                        </button>
                      </div>
                    )}
                  </div>
                  <button onClick={() => remove(p)} data-testid={`del-${p.id}`} style={{ color: "var(--muted)" }}>
                    <Trash2 size={15} />
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  <Metric k={tr("PEAK", "POINTE")} v={p.input?.peak_kw ? `${unit(p.input.peak_kw, "kW", 3)}` : "—"} testid={`metric-peak-${p.id}`} />
                  <Metric k="CAPEX" v={usd(p.result?.capex?.total)} />
                  <Metric k="LCOE" v={getLocale() === "fr" && p.result?.financials?.lcoe_usd_per_kwh ? perUnit(p.result.financials.lcoe_usd_per_kwh, "kWh", 3, 0) : `$${p.result?.financials?.lcoe_usd_per_kwh || "—"}/kWh`} />
                  <Metric k={tr("Renewable", "Renouvelable")} v={pct(Math.round((p.result?.model?.renewable_fraction || 0) * 100), 0)} />
                  <Metric k={tr("Payback", "Retour sur investissement")} v={comparisonYears(p.result?.financials?.simple_payback_years, getLocale())} />
                </div>
                <div className="flex items-center justify-between mt-5 flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {p.paid ? (
                      <span className="pill" style={{ background: "#e8f5e9", color: "var(--brand-green)" }} data-testid={`status-ready-${p.id}`}>{tr("Report ready", "Rapport prêt")}</span>
                    ) : (
                      <span className="pill" style={{ background: "var(--paper)", color: "var(--muted)" }}><Lock size={11} /> {tr("Locked", "Verrouillé")}</span>
                    )}
                    {/* iter45n — "Updated X ago" chip surfaces whenever
                         the project's `updated_at` is newer than its
                         original `created_at` — i.e. the buyer used the
                         Re-edit flow. Small and low-contrast so it
                         complements rather than competes with the
                         Locked / Ready state. Absolute ISO timestamp
                         goes into `title` for lenders who want the
                         exact time on hover. */}
                    {p.updated_at && p.created_at && p.updated_at > p.created_at && (
                      <span className="pill" style={{ background: "#EEF3FF", color: "#2A4E8F" }}
                             title={new Date(p.updated_at).toLocaleString()}
                             data-testid={`status-reedited-${p.id}`}>
                        {tr("Updated", "Modifié")} {formatRelativeTime(p.updated_at)}
                      </span>
                    )}
                    {p.paid && versionStatus[p.id]?.has_report && !versionStatus[p.id]?.live_matches_latest && (
                      <span className="pill" style={{ background: "#FFE7C4", color: "#873F00" }} data-testid={`status-updated-${p.id}`} title={tr("Your saved report still downloads below. Buy a new report to reflect the latest inputs.", "Votre rapport enregistré reste téléchargeable ci-dessous. Achetez un nouveau rapport pour refléter les dernières données.")}>{tr("Inputs updated · new report optional", "Données modifiées · nouveau rapport facultatif")}</span>
                    )}
                  </div>
                  <button onClick={() => openReport(p)} className="btn-ghost !p-0" data-testid={`open-${p.id}`}>
                    {p.paid ? tr("Open report →", "Ouvrir le rapport →") : (user?.credits > 0 ? tr("Unlock with 1 report credit →", "Déverrouiller avec 1\u00a0crédit →") : tr("Buy report →", "Acheter le rapport →"))}
                  </button>
                </div>
                {!p.paid && (
                  // iter45l — Re-edit lets a buyer return to the wizard
                  // with every input pre-filled so they can adjust before
                  // paying. Sits below the Buy CTA as a secondary text
                  // link so the paid conversion stays the primary action.
                  <div className="mt-2 flex justify-end">
                    <button
                      onClick={() => {
                        sessionStorage.setItem("configurator_prefill",
                          JSON.stringify({ input: p.input, project_id: p.id }));
                        navigate("/configurator");
                      }}
                      className="text-xs underline underline-offset-2"
                      style={{ color: "var(--muted)", background: "transparent",
                                border: "none", cursor: "pointer", padding: 0 }}
                      data-testid={`reedit-${p.id}`}
                    >
                      {tr("Re-edit configuration", "Modifier la configuration")}
                    </button>
                  </div>
                )}
                {p.paid && (
                  <div className="mt-3 pt-3 flex flex-wrap gap-2 text-xs items-center" style={{ borderTop: "1px solid var(--hairline)" }}>
                    <div
                      className="inline-flex items-stretch rounded overflow-hidden"
                      style={{ border: "1px solid var(--hairline)" }}
                      data-testid={`pdf-lang-toggle-${p.id}`}
                    >
                      {["en", "fr"].map((code) => {
                        const active = (pdfLangByProject[p.id] || currentAppLang()) === code;
                        return (
                          <button
                            key={code}
                            type="button"
                            onClick={() => setPdfLangByProject((s) => ({ ...s, [p.id]: code }))}
                            className="!py-1.5 !px-2 text-[10px] font-bold uppercase tracking-wider"
                            style={{
                              background: active ? "var(--brand-green)" : "var(--white)",
                              color: active ? "#fff" : "var(--muted)",
                              borderLeft: code === "fr" ? "1px solid var(--hairline)" : undefined,
                            }}
                            data-testid={`pdf-lang-${p.id}-${code}`}
                            aria-pressed={active}
                          >
                            {code}
                          </button>
                        );
                      })}
                    </div>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${p.id}/pdf`}
                      onClick={(e) => {
                        // Rewrite the href at click time so the URL reflects the
                        // customer's CURRENT language toggle (users may switch
                        // EN↔FR mid-session without a page reload). Per-card
                        // override wins over the on-screen language.
                        const lang = pdfLangByProject[p.id] || currentAppLang();
                        e.currentTarget.href = `${process.env.REACT_APP_BACKEND_URL}/api/projects/${p.id}/pdf?lang=${lang}`;
                        e.preventDefault();
                        downloadPdf(e.currentTarget.href, `Power_Configurator_${(p.name || "report").replace(/[^a-z0-9-_ ]/gi, "_")}_${lang.toUpperCase()}.pdf`);
                      }}
                      download
                      className="btn-secondary !py-1.5 !px-3 text-xs"
                      data-testid={`pdf-${p.id}`}
                    >
                      <FileText size={12} /> PDF
                    </a>
                    <div className="inline-flex items-stretch rounded overflow-hidden" style={{ border: "1px solid var(--hairline)" }}>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${p.id}/csv`}
                        onClick={(e) => {
                          // Match the PDF button pattern: rewrite the href at
                          // click time so `?lang=` reflects the card's current
                          // toggle. The CSV data itself is language-neutral;
                          // `lang` currently only flips the XLSX-upsell hint.
                          const lang = pdfLangByProject[p.id] || currentAppLang();
                          e.currentTarget.href = `${process.env.REACT_APP_BACKEND_URL}/api/projects/${p.id}/csv?lang=${lang}`;
                        }}
                        download
                        className="!py-1.5 !px-3 text-xs flex items-center gap-1.5"
                        style={{ background: "var(--white)", color: "var(--char)" }}
                        data-testid={`csv-${p.id}`}
                        title={tr("Download all sections as CSV", "Télécharger toutes les sections en CSV")}
                      >
                        <FileText size={12} /> CSV
                      </a>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="!py-1.5 !px-2 text-xs flex items-center"
                            style={{ background: "var(--white)", color: "var(--char)", borderLeft: "1px solid var(--hairline)" }}
                            data-testid={`csv-preset-${p.id}`}
                            title={tr("Download a specific section", "Télécharger une section précise")}
                            aria-label={tr("Choose CSV preset", "Choisir un préréglage CSV")}
                          >
                            <ChevronDown size={12} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="text-xs">
                          <DropdownMenuLabel>{tr("Download preset", "Préréglage de téléchargement")}</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem asChild>
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${p.id}/csv?lang=${pdfLangByProject[p.id] || currentAppLang()}`} download
                                data-testid={`csv-preset-${p.id}-all`}>
                              <span className="flex flex-col">
                                <span className="font-medium">{tr("Full export", "Export complet")}</span>
                                <span style={{ color: "var(--muted)" }}>{tr("All sections", "Toutes les sections")}</span>
                              </span>
                            </a>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${p.id}/csv?columns=finance&lang=${pdfLangByProject[p.id] || currentAppLang()}`} download
                                data-testid={`csv-preset-${p.id}-finance`}>
                              <span className="flex flex-col">
                                <span className="font-medium">Finance</span>
                                <span style={{ color: "var(--muted)" }}>{tr("CAPEX + OPEX + Financials — for board decks", "CAPEX + OPEX + Finances — pour vos présentations")}</span>
                              </span>
                            </a>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${p.id}/csv?columns=technical&lang=${pdfLangByProject[p.id] || currentAppLang()}`} download
                                data-testid={`csv-preset-${p.id}-technical`}>
                              <span className="flex flex-col">
                                <span className="font-medium">{tr("Technical", "Technique")}</span>
                                <span style={{ color: "var(--muted)" }}>{tr("Inputs + Model + Equipment", "Données d'entrée + Modèle + Équipements")}</span>
                              </span>
                            </a>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${p.id}/csv?columns=permitting&lang=${pdfLangByProject[p.id] || currentAppLang()}`} download
                                data-testid={`csv-preset-${p.id}-permitting`}>
                              <span className="flex flex-col">
                                <span className="font-medium">{tr("Permitting", "Réglementation")}</span>
                                <span style={{ color: "var(--muted)" }}>{tr("Permitting + Milestones", "Réglementation + Jalons")}</span>
                              </span>
                            </a>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="inline-flex items-stretch rounded overflow-hidden" style={{ border: "1px solid var(--hairline)" }}>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${p.id}/xlsx`}
                        download
                        className="!py-1.5 !px-3 text-xs flex items-center gap-1.5"
                        style={{ background: "var(--white)", color: "var(--char)" }}
                        data-testid={`xlsx-${p.id}`}
                        title={tr("Branded Excel workbook — 9 tabs with live CAPEX/OPEX formulas", "Classeur Excel à nos couleurs — 9\u00a0onglets avec formules CAPEX/OPEX actives")}
                      >
                        <FileSpreadsheet size={12} /> Excel
                      </a>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="!py-1.5 !px-2 text-xs flex items-center"
                            style={{ background: "var(--white)", color: "var(--char)", borderLeft: "1px solid var(--hairline)" }}
                            data-testid={`xlsx-preset-${p.id}`}
                            title={tr("Download a slim workbook preset", "Télécharger un classeur allégé")}
                            aria-label={tr("Choose Excel preset", "Choisir un préréglage Excel")}
                          >
                            <ChevronDown size={12} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="text-xs">
                          <DropdownMenuLabel>{tr("Download preset", "Préréglage de téléchargement")}</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem asChild>
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${p.id}/xlsx`} download data-testid={`xlsx-preset-${p.id}-all`}>
                              <span className="flex flex-col">
                                <span className="font-medium">{tr("Full workbook", "Classeur complet")}</span>
                                <span style={{ color: "var(--muted)" }}>{tr("All 9 tabs", "Les 9\u00a0onglets")}</span>
                              </span>
                            </a>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${p.id}/xlsx?columns=finance`} download data-testid={`xlsx-preset-${p.id}-finance`}>
                              <span className="flex flex-col">
                                <span className="font-medium">Finance</span>
                                <span style={{ color: "var(--muted)" }}>{tr("Summary + CAPEX + OPEX — one-page board sends", "Synthèse + CAPEX + OPEX — envoi d'une page pour le conseil")}</span>
                              </span>
                            </a>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${p.id}/xlsx?columns=technical`} download data-testid={`xlsx-preset-${p.id}-technical`}>
                              <span className="flex flex-col">
                                <span className="font-medium">{tr("Technical", "Technique")}</span>
                                <span style={{ color: "var(--muted)" }}>{tr("Summary + Inputs + Power model + Equipment", "Synthèse + Données d'entrée + Modèle énergétique + Équipements")}</span>
                              </span>
                            </a>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${p.id}/xlsx?columns=permitting`} download data-testid={`xlsx-preset-${p.id}-permitting`}>
                              <span className="flex flex-col">
                                <span className="font-medium">{tr("Permitting", "Réglementation")}</span>
                                <span style={{ color: "var(--muted)" }}>{tr("Summary + Roadmap + Permitting", "Synthèse + Feuille de route + Réglementation")}</span>
                              </span>
                            </a>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── Recently deleted bin ─────────────────────────────────────────
            Clearly separated from active projects; 7-day retention with a
            Restore button per row. Auto-purged server-side by an hourly job. */}
        {deletedProjects.length > 0 && (
          <div className="mt-16" data-testid="deleted-section">
            <div className="flex items-center gap-2 mb-2">
              <Archive size={14} style={{ color: "var(--muted)" }} />
              <h2 className="font-display text-lg" style={{ color: "var(--char)" }}>
                {tr("Recently deleted", "Récemment supprimés")}{" "}
                <span className="pill" style={{ background: "var(--paper)", color: "var(--muted)" }}
                        data-testid="deleted-count-badge">
                  {deletedProjects.length}
                </span>
              </h2>
            </div>
            <p className="text-xs mb-4" style={{ color: "var(--muted)" }}>
              {tr("Items are permanently removed 7 days after deletion. Paid reports remain in", "Les éléments sont définitivement supprimés 7\u00a0jours après leur suppression. Les rapports payés restent dans")}{" "}
              <a href="/reports" className="underline">{tr("My Unlocked Reports", "Mes rapports déverrouillés")}</a> {tr("regardless of the bin expiry.", "quelle que soit l'expiration de la corbeille.")}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="deleted-list">
              {deletedProjects.map((p) => {
                const country = p.input?.country || "Unknown";
                const sector = p.input?.sector || "Unknown";
                const deletedDate = (p.deleted_at || "").slice(0, 10);
                const expiryDate = (p.deleted_expires_at || "").slice(0, 10);
                return (
                  <div
                    key={p.id}
                    className="card p-3 flex items-center justify-between gap-3"
                    style={{ background: "var(--paper)", border: "1px dashed var(--hairline)" }}
                    data-testid={`deleted-row-${p.id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate" style={{ color: "var(--char)" }}>
                        {p.name || tr("Untitled Project", "Projet sans titre")}
                        {p.paid && (
                          <span className="ml-2 pill" style={{ background: "#e8f5e9", color: "var(--brand-green)", fontSize: "0.6rem" }}>
                            {tr("Paid report kept in Unlocked Reports", "Rapport payé conservé dans les rapports déverrouillés")}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] mt-0.5" style={{ color: "var(--muted)" }}>
                        <Building2 size={10} className="inline -mt-0.5" /> {sectorName(sector)} ·{" "}
                        <Globe size={10} className="inline -mt-0.5" /> {getLocale() === "fr" ? t(`country.${country}`, country) : country}
                      </div>
                      <div className="text-[11px] mt-0.5" style={{ color: "var(--muted)" }}>
                        {tr("Deleted", "Supprimé le")} {deletedDate} · {tr("Permanently removed on", "Suppression définitive le")}{" "}
                        <span style={{ color: "#873F00" }}>{expiryDate}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => restore(p)}
                      className="btn-secondary !py-1.5 !px-3 text-xs shrink-0"
                      data-testid={`restore-${p.id}`}
                    >
                      <RotateCcw size={12} /> {tr("Restore", "Restaurer")}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <AlertDialog
        open={deleteConfirm.open}
        onOpenChange={(o) => !o && !deleteConfirm.busy && setDeleteConfirm({ open: false, project: null, busy: false })}
      >
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle size={16} style={{ color: "#873F00" }} />
              {tr("Delete this project?", "Supprimer ce projet\u00a0?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {tr("It will be moved to your Recently deleted bin and permanently removed after 7 days. You can restore it any time within that window.", "Il sera déplacé dans la corbeille Récemment supprimés et définitivement supprimé après 7\u00a0jours. Vous pouvez le restaurer à tout moment pendant ce délai.")}
              {deleteConfirm.project?.paid && (
                <span className="block mt-3 p-2 rounded text-xs"
                        style={{ background: "#e8f5e9", color: "#1A6F2B", border: "1px solid var(--brand-green)" }}
                        data-testid="delete-confirm-paid-note">
                  {tr("Reminder: your paid report will remain available in My Unlocked Reports even after this project is permanently removed.", "Rappel\u00a0: votre rapport payé restera disponible dans Mes rapports déverrouillés, même après la suppression définitive de ce projet.")}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteConfirm.busy} data-testid="delete-confirm-cancel">{tr("Cancel", "Annuler")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteConfirm.busy}
              data-testid="delete-confirm-move"
            >
              {tr("Move to bin", "Déplacer vers la corbeille")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PurchaseModal
        open={purchaseModal.open}
        onClose={() => setPurchaseModal({ open: false, projectId: null })}
        lockedProjects={projects.filter((p) => !p.paid)}
        startProjectId={purchaseModal.projectId}
        onPurchaseComplete={() => { checkAuth && checkAuth(); load(); setPurchaseModal({ open: false, projectId: null }); }}
      />
      <ConfirmUnlockModal
        open={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, project: null })}
        project={confirmModal.project}
        walletBefore={user?.credits || 0}
        onUnlocked={() => { checkAuth && checkAuth(); load(); }}
      />
      <Footer />
    </div>
  );
};

const Metric = ({ k, v, testid }) => (
  <div className="p-2 rounded-md" data-testid={testid} style={{ background: "var(--paper)" }}>
    <div className="overline" style={{ fontSize: "0.6rem" }}>{k}</div>
    <div className="font-mono" style={{ color: "var(--charcoal)" }}>{v}</div>
  </div>
);
